// SPDX-License-Identifier: MIT
/**
 * Kinetic typography: line wrapping for mixed Latin/CJK copy, size fitting, masked line reveals,
 * per-glyph animation and decode/scramble effects. Text is the main carrier of a brand film's
 * message, so these get the most care.
 */
import { hash01 } from '../core/rng.ts';
import { type GlyphState, chars, drawGlyphs } from '../type.ts';
export type Line = {
    text: string;
    width: number;
};
/** Code-point ranges set one glyph per wrap unit: CJK ideographs, kana, hangul, fullwidth forms. */
const CJK_RANGES: Array<[
    number,
    number
]> = [
    [0x2e80, 0x9fff],
    [0xac00, 0xd7af],
    [0xf900, 0xfaff],
    [0xff00, 0xffef],
    [0x3000, 0x303f],
];
const isCJK = (c: string) => {
    const cp = c.codePointAt(0) ?? 0;
    return CJK_RANGES.some(([a, b]) => cp >= a && cp <= b);
};
/** Characters that must not start a line in CJK typesetting (kinsoku). */
const NO_LINE_START = /^[，。！？、；：,.!?;:)\]}」』）】》〉"'’”…]/;
/** Break text into wrap units: Latin words keep their trailing space; each CJK character is a unit. */
function units(text: string): string[] {
    const out: string[] = [];
    let word = '';
    for (const c of chars(text)) {
        if (isCJK(c)) {
            if (word)
                out.push(word);
            word = '';
            out.push(c);
        }
        else if (c === ' ') {
            out.push(word + c);
            word = '';
        }
        else
            word += c;
    }
    if (word)
        out.push(word);
    return out;
}
/** Greedy line wrap with the current ctx.font. Explicit "\n" always breaks. */
export function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): Line[] {
    const lines: Line[] = [];
    for (const para of text.split('\n')) {
        let line = '';
        for (const u of units(para)) {
            const candidate = line + u;
            if (line && ctx.measureText(candidate.trimEnd()).width > maxWidth && !NO_LINE_START.test(u)) {
                lines.push({ text: line.trimEnd(), width: ctx.measureText(line.trimEnd()).width });
                line = u.trimStart();
            }
            else
                line = candidate;
        }
        lines.push({ text: line.trimEnd(), width: ctx.measureText(line.trimEnd()).width });
    }
    return lines;
}
/** Largest font size in [minPx, maxPx] at which `text` fits `maxWidth` on `maxLines` lines. */
export function fitSize(ctx: CanvasRenderingContext2D, text: string, fontAt: (px: number) => string, maxWidth: number, { maxPx = 200, minPx = 24, maxLines = 1 }: {
    maxPx?: number;
    minPx?: number;
    maxLines?: number;
} = {}): number {
    let lo = minPx;
    let hi = maxPx;
    const fits = (px: number) => {
        ctx.font = fontAt(px);
        const lines = wrap(ctx, text, maxWidth);
        return lines.length <= maxLines && lines.every((l) => l.width <= maxWidth);
    };
    if (fits(hi))
        return hi;
    for (let i = 0; i < 12; i++) {
        const mid = (lo + hi) / 2;
        if (fits(mid))
            lo = mid;
        else
            hi = mid;
    }
    ctx.font = fontAt(lo);
    return Math.floor(lo);
}
export type RevealOptions = {
    x: number;
    y: number;
    lineHeight: number;
    align?: 'left' | 'center' | 'right';
    /** Per-line progress 0–1 (index → progress). Use beat.seg + stagger to build it. */
    progress: (line: number) => number;
    /** How far (in lineHeights) the line rises from below its mask. Default 1. */
    rise?: number;
};
/**
 * Masked line reveal: each line slides up from behind its own clipping box — the "editorial"
 * title move. The text baseline of line i is y + i * lineHeight.
 */
export function revealLines(ctx: CanvasRenderingContext2D, lines: Line[], opts: RevealOptions): void {
    const { x, y, lineHeight, align = 'left', rise = 1 } = opts;
    ctx.save();
    ctx.textAlign = align;
    ctx.textBaseline = 'alphabetic';
    lines.forEach((line, i) => {
        const k = opts.progress(i);
        if (k <= 0)
            return;
        const baseline = y + i * lineHeight;
        const left = align === 'left' ? x : align === 'center' ? x - line.width / 2 : x - line.width;
        ctx.save();
        ctx.beginPath();
        ctx.rect(left - lineHeight, baseline - lineHeight * 0.95, line.width + lineHeight * 2, lineHeight * 1.25);
        ctx.clip();
        ctx.fillText(line.text, x, baseline + (1 - k) * lineHeight * rise);
        ctx.restore();
    });
    ctx.restore();
}
/**
 * Decode / scramble: glyphs resolve from random characters to the final text. `k` (0–1) is how far
 * the resolve front has travelled; unresolved glyphs flicker through `charset`, changing `rate`
 * times per second of `t`. Deterministic for a given (seed, t).
 */
export function decode(text: string, k: number, t: number, { seed = 1, rate = 24, charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*+<>/' } = {}): string {
    const list = chars(text);
    const tick = Math.floor(t * rate);
    return list
        .map((c, i) => {
        if (c === ' ' || i / list.length < k)
            return c;
        if (i / list.length > k + 0.35)
            return ' ';
        return charset[Math.floor(hash01(i, tick, seed) * charset.length)]!;
    })
        .join('');
}
/** Per-glyph animation helper (re-export with a friendlier name for scene authors). */
export function glyphs(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, align: 'left' | 'center' | 'right', state: (index: number, count: number) => GlyphState): number {
    return drawGlyphs(ctx, text, x, y, align, state);
}
/** Visible duration (seconds) a line needs to be read: max(0.5 s, glyphs / 15 per s) (§7.10). */
export function readingTime(text: string): number {
    return Math.max(0.5, chars(text.replace(/\s+/g, '')).length / 15);
}
/** Splits "87.5%" into prefix / number / suffix so the number can count up with its formatting. */
export function parseStat(value: string): {
    prefix: string;
    n: number;
    decimals: number;
    suffix: string;
} | null {
    const m = /^([^\d-]*)(-?\d[\d,]*(?:\.\d+)?)(.*)$/.exec(value);
    if (!m)
        return null;
    const digits = m[2]!.replace(/,/g, '');
    return {
        prefix: m[1]!,
        n: Number(digits),
        decimals: digits.includes('.') ? digits.split('.')[1]!.length : 0,
        suffix: m[3]!,
    };
}
